/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* eslint-disable no-bitwise */
/* global document window */

import {
	default as JSG,
	CellRange,
	SheetReference,
	Selection,
	Point,
	MathUtils,
	GraphUtils,
	Numbers,
	TextFormatAttributes,
	SheetPlotNode,
	ChartRect,
	FormatAttributes
} from '@cedalo/jsg-core';

import { FuncTerm, NullTerm } from '@cedalo/parser';

import { NumberFormatter } from '@cedalo/number-format';

import CellEditor from './CellEditor';
import NodeView from './NodeView';
import { createView } from '@cedalo/jsg-extensions/ui';
import ContentPaneView from './ContentPaneView';
import ContentNodeView from './ContentNodeView';

const defaultCellErrorValue = '#####';

const drawMarker = (graphics, color, pts) => {
	graphics.setFillColor(color);
	graphics.fillPolyline(pts, true);
	graphics.setFillColor('#000000');
};

const getAsyncFuncName = (funcTerm) => {
	const param = funcTerm.getParamAt(0);
	return FuncTerm.showName(param) ? `ASYNC.${param.name}` : 'ASYNC';
};

/**
 * This view is for a {{#crossLink "CellsNode"}}{{/crossLink}} model. Although it
 * can be instantiated directly the recommended way to create thi s view is by calling
 * {{#crossLink "NodeController/createView:method"}}{{/crossLink}} method.
 *
 * @class CellsView
 * @extends NodeView
 * @param {CellsNode} item The corresponding CellsNode model.
 * @constructor
 */
export default class CellsView extends NodeView {
	drawBorder(graphics, format, rect) {
		this._wsView = this.getWorksheetView();
		this._wsItem = this.getWorksheetNode();
		this._columns = this.getColumns();
		this._rows = this.getRows();
		if (!this._wsView || !this._wsItem || this._wsItem.getLayoutNode()) {
			return;
		}

		const graph = this._wsItem.getGraph();
		const viewRect = this._wsView.getViewPort().getVisibleViewRect();

		this._greyIfRows = this._wsItem.model.greyIfRows;
		this._showFormulas = this._wsItem.model.showFormulas;
		this._editMode = graph.machineContainer && !graph.machineContainer.isRunning;

		if (this._wsItem.isGridVisible()) {
			this.drawGrid(graphics, rect, viewRect);
		}
		graphics.setTransparency(100);

		this.drawData(graphics, rect, viewRect);

		const id = this.getGraphView().getActiveViewId();
		if (id !== undefined && id !== this._wsItem.getId()) {
			this.drawSelections(graphics, rect);
		}
		this.drawCellEditRanges(graphics, rect);
		if (id !== undefined && id !== this._wsItem.getId()) {
			this.drawCopyMarker(graphics, rect);
		}

		this.drawPredecessors(graphics, rect);
		this.drawDependants(graphics, rect);
		this.drawSearchResult(graphics);
		this.drawTourHighlights(graphics);
	}

	drawFill(graphics, format, rect) {
		this._wsItem = this.getWorksheetNode();
		if (!this._wsItem || this._wsItem.getLayoutNode()) {
			return;
		}

		super.drawFill(graphics, format, rect);
	}

	drawData(graphics, rect, viewRect) {
		const dataProvider = this._wsItem.model.cells;

		// get visible matrix
		const visibleColumnsInfo = this.getVisibleColumnSections(rect, viewRect);
		const visibleRowsInfo = this.getVisibleRowsSections(rect, viewRect, visibleColumnsInfo);

		if (visibleColumnsInfo.length === 0 || visibleRowsInfo.length === 0) {
			return;
		}

		this.setFont(graphics);
		graphics.setFillColor(JSG.theme.text);
		graphics.setTextBaseline('bottom');

		// draw cell content
		visibleRowsInfo.forEach((rowInfo) => {
			visibleColumnsInfo.forEach((columnInfo) => {
				const data = dataProvider.getRC(columnInfo.index, rowInfo.index);
				const cellProperties = this.getCellProperties(data, columnInfo, rowInfo);
				const styleProperties = this.getStyleProperties(data, columnInfo, rowInfo);
				this.drawCellFill(graphics, data,  columnInfo, rowInfo, visibleColumnsInfo, visibleRowsInfo, styleProperties, cellProperties);
			});
			if (rowInfo.leftCellInfo) {
				const data = dataProvider.getRC(rowInfo.leftCellInfo.index, rowInfo.index);
				if (data !== undefined && data.getExpression() !== undefined) {
					this.drawValue(graphics, dataProvider, data, rowInfo.leftCellInfo, rowInfo, visibleColumnsInfo);
				}
			}
			visibleColumnsInfo.forEach((columnInfo) => {
				const data = dataProvider.getRC(columnInfo.index, rowInfo.index);
				if (data !== undefined) {
					if (data.getExpression() !== undefined) {
						const cellProperties = this.getCellProperties(data, columnInfo, rowInfo);
						const styleProperties = this.getStyleProperties(data, columnInfo, rowInfo);
						this.drawValue(graphics, dataProvider, data, columnInfo, rowInfo, visibleColumnsInfo,
							styleProperties, cellProperties);
						this.drawSpecialFunction(graphics, data, columnInfo, rowInfo, visibleColumnsInfo,
							visibleRowsInfo);
					}
					this.drawInfoMarkers(graphics, data, columnInfo, rowInfo);
				}
			});
			if (rowInfo.rightCellInfo) {
				const data = dataProvider.getRC(rowInfo.rightCellInfo.index, rowInfo.index);
				if (data !== undefined && data.getExpression() !== undefined) {
					this.drawValue(graphics, dataProvider, data, rowInfo.rightCellInfo, rowInfo, visibleColumnsInfo);
				}
			}
		});

		let last;

		const borders = {
			grid: []
		};

		const sheetHeight = Math.min(this._rows.getSectionPos(this._wsItem.model.maxRows), viewRect.y + viewRect.height);
		// draw grid
		if (this._wsItem.isGridVisible()) {
			visibleColumnsInfo.forEach((columnInfo) => {
				last = viewRect.y;

				columnInfo.gridSkipBorders.forEach((info, index) => {
					if (last !== info.y && info.y > last) {
						borders.grid.push({
							x1: columnInfo.x,
							y1: last,
							x2: columnInfo.x,
							y2: info.y
						});
					}
					last = info.y + info.height;
				});

				borders.grid.push({
					x1: columnInfo.x,
					y1: last,
					x2: columnInfo.x,
					y2: sheetHeight
				});
			});
		}

		graphics.beginPath();
		graphics.setLineColor(JSG.theme.grid);
		borders.grid.forEach((line) => {
			graphics.moveTo(line.x1, line.y1);
			graphics.lineTo(line.x2, line.y2);
		});
		graphics.stroke();

		graphics.setLineCap('square');

		// draw borders
		visibleColumnsInfo.forEach((columnInfo) => {
			Object.entries(columnInfo.leftBorders).forEach(([color, value]) => {
				graphics.setLineColor(color);
				Object.entries(value).forEach(([y, border]) => {
					if (columnInfo.skipBorders[y] === undefined) {
						graphics.beginPath();
						graphics.setLineWidth(
							border.borderWidth === 1 || border.borderWidth === undefined
								? 1
								: graphics.getCoordinateSystem().deviceToLogX(border.borderWidth)
						);
						graphics.setLineStyle(border.style === undefined ? 1 : border.style);
						graphics.applyLineDash();
						graphics.moveTo(columnInfo.x, Number(y));
						graphics.lineTo(columnInfo.x, Number(y) + border.height);
						graphics.stroke();
						graphics.clearLineDash();
					}
				});
			});
		});

		visibleRowsInfo.forEach((rowInfo) => {
			Object.entries(rowInfo.topBorders).forEach(([color, value]) => {
				graphics.setLineColor(color);
				Object.entries(value).forEach(([x, border]) => {
					graphics.beginPath();
					graphics.setLineWidth(
						border.borderWidth === 1 || border.borderWidth === undefined
							? 1
							: graphics.getCoordinateSystem().deviceToLogX(border.borderWidth)
					);
					graphics.setLineStyle(border.style === undefined ? 1 : border.style);
					graphics.applyLineDash();
					graphics.moveTo(Number(x), rowInfo.y);
					graphics.lineTo(Number(x) + border.width, rowInfo.y);
					graphics.stroke();
					graphics.clearLineDash();
				});
			});
		});
		graphics.setLineCap('butt');
	}

	getFormattedValueFromFunction(graphics, data, result, availableWidth) {
		const term = data.getExpression().getTerm();
		if (FuncTerm.showName(term)) {
			const funcId = term.getFuncId();
			switch (funcId) {
				case 'SELECT':
					result.clip = true;
					result.forcedAlignment = 0;
					result.clipSpace = 600;
					result.value = String(result.value);
					if (typeof result.value === 'string') {
						let width = graphics
							.getCoordinateSystem()
							.deviceToLogX(graphics.measureText(result.value).width, true);
						const eWidth = graphics
							.getCoordinateSystem()
							.deviceToLogX(graphics.measureText('...').width, true);
						if (width + 150 > availableWidth - result.clipSpace && result.formattedValue.length) {
							while (width + eWidth + 150 > availableWidth - result.clipSpace && result.formattedValue.length) {
								result.formattedValue = result.formattedValue.slice(0, result.formattedValue.length - 1)
								width = graphics
									.getCoordinateSystem()
									.deviceToLogX(graphics.measureText(result.formattedValue).width, true);
							}
							result.formattedValue += '...';
						}
					}
					break;
				case 'BAR':
				case 'CELLCHART':
					result.value = '';
					result.formattedValue = '';
					break;
				case 'READ':
				case 'WRITE':
					result.clip = true;
					result.fillColor = '#AAAAAA';
					result.color = '#FFFFFF';
					result.bold = true;
					result.rounded = funcId === 'READ' ? 'left' : 'right';
					if (term.params && term.params.length) {
						const ib = term.params[0];
						if ((ib instanceof FuncTerm) &&
							ib.name === 'INBOXDATA' &&
							ib.params.length > 2 &&
							(ib.params[2] instanceof NullTerm)) {
							result.loop = true;
						}
					}
					if (term.params && term.params.length > 2) {
						const value = term.params[2].value;
						// default for READ is "json" => better pass type with cell in future version!
						const type = !value && funcId === 'READ' ? 'json' : `${value}`.toLowerCase();
						switch (type) {
						case 'string':
							result.fillColor = '#49af57';
							break;
						case 'number':
							result.fillColor = '#73ABCB';
							break;
						case 'bool':
						case 'boolean':
							result.fillColor = '#6DD0BA';
							break;
						case 'dictionary':
						case 'json':
						case 'jsonroot':
							result.fillColor = '#FD602E';
							break;
						case 'array':
							result.fillColor = '#8676D3';
							// result.fillColor = '#9360E7';
							break;
						default:
							break;
						}
					}
					break;
				default: {
					const name = funcId === 'ASYNC' ? getAsyncFuncName(term) : funcId;
					result.clip = true;
					result.value = String(data.getValue());
					result.rounded = 'full';
					if (result.value === 'true' || result.value[0] !== '#') {
						result.fillColor = '#3C5280';
						result.value = name;
						result.formattedValue = name;
					} else {
						result.fillColor = '#E25B5D';
					}
					result.color = '#FFFFFF';
					result.bold = true;
				}
			}
			// shouldn't we return true here?
			// return true;
		}
		return false;
	}

	replaceLocalField(graphics, data, result) {
		if (!(typeof result.value === 'string')) {
			return;
		}

		if (result.value === '#[LocalDate]') {
			result.value = MathUtils.nowAsSerial(); // JSDateToExcelDate(new Date());
		}
	}

	getFormattedValue(graphics, data, textproperties, styleProperties, availableWidth) {
		const result = {
			value: data.getValue(),
			formattedValue: data.getValue(),
			type: 'general',
			clip: false
		};

		if (this._showFormulas) {
			const expr = data.getExpression();
			// if (expr.hasFormula()) {
				expr.evaluate(this._wsItem);
				result.value = expr.toLocaleString(JSG.getParserLocaleSettings(), {
					sheet: this._wsItem.model,
					useName: true
				});
			// } else if (result.value === undefined) {
			// 	result.value = '#NV';
			// }
			result.formattedValue = result.value;
			result.alignment = 0;
			result.forcedAlignment = 0;
			return result;
		}

		this.replaceLocalField(graphics, data, result);

		if (this.getFormattedValueFromFunction(graphics, data, result, availableWidth)) {
			return result;
		}

		if (textproperties.fontcolor) {
			result.color = textproperties.fontcolor;
		}
		if (styleProperties && styleProperties.fillstyle === 1 && styleProperties.fillcolor) {
			result.fillColor = styleProperties.fillcolor;
		}

		if (typeof result.value === 'boolean') {
			result.formattedValue = result.value.toString().toUpperCase();
			availableWidth = Math.max(availableWidth - 200, 0);
			const width = graphics
				.getCoordinateSystem()
				.deviceToLogX(graphics.measureText(result.formattedValue).width, true);
			if (width > availableWidth) {
				result.formattedValue = new Array(Math.floor(availableWidth / 150) + 1).join('#');
			}
			result.clip = true;
			result.alignment = 1;
			return result;
		}

		if (!Numbers.isNumber(result.value) || result.value === undefined) {
			if (typeof result.value === 'string' && result.value[0] === '#') {
				result.alignment = 1;
			}
			return result;
		}

		const numberFormat = textproperties && textproperties.numberformat ? textproperties.numberformat : 'General';
		const localCulture = textproperties && textproperties.localculture ? textproperties.localculture : 'general';
		const type = localCulture.split(';');

		try {
			const newResult = NumberFormatter.formatNumber(numberFormat, result.value, type[0]);
			result.formattedValue = newResult.formattedValue;
			result.value = newResult.value;
			if (newResult.color !== null) {
				result.color = newResult.color;
			}
		} catch (e) {
			result.formattedValue = defaultCellErrorValue;
		}
		[result.type] = type;
		if (result.formattedValue === '' && (result.type === 'date' || result.type === 'time')) {
			result.formattedValue = `#INVALID_${result.type.toUpperCase()}`;
			result.value = result.formattedValue;
		}

		availableWidth = Math.max(availableWidth - 200, 0);
		let width = graphics
			.getCoordinateSystem()
			.deviceToLogX(graphics.measureText(result.formattedValue).width, true);
		if (width > availableWidth) {
			if (result.type === 'general' && !Number.isInteger(result.value)) {
				const decPart = String(result.value).split('.')[1];
				let decimals = decPart ? decPart.length - 1 : 0;

				while (decimals > 0 && width > availableWidth) {
					const newResult = NumberFormatter.formatNumber('General', MathUtils.roundTo(result.value, decimals), 'general');
					result.formattedValue = newResult.formattedValue;
					result.value = newResult.value;
					if (newResult.color !== null) {
						result.color = newResult.color;
					}

					width = graphics
						.getCoordinateSystem()
						.deviceToLogX(graphics.measureText(result.formattedValue).width, true);
					decimals -= 1;
				}
			}
			if (width > availableWidth) {
				result.formattedValue = new Array(Math.floor(availableWidth / 150) + 1).join('#');
			}
			result.clip = true;
		}

		if (Numbers.isNumber(result.value)) {
			// DL-6279: numbers as text should align left
			result.alignment = result.type === 'text' ? 0 : 2;
		}

		return result;
	}

	applyFormatFromKey(dataProvider, data, columnInfo, rowInfo, formattedValue, cellProperties) {
		if (cellProperties && cellProperties.key) {
			const dataNext = dataProvider.getRC(columnInfo.index + 1, rowInfo.index);
			if (dataNext === undefined) {
				return;
			}
			const columnInfoTmp = {
				index: columnInfo.index + 1
			}
			let cellPropertiesTmp = this.getCellProperties(dataNext, columnInfoTmp, rowInfo);
			if (!cellPropertiesTmp || !cellPropertiesTmp.key) {
				return;
			}

			formattedValue.level = cellProperties.level || 0;
			formattedValue.alignment = 0;
			formattedValue.clip = true;
			formattedValue.color = '#FFFFFF';
			formattedValue.frame = true;

			const rowInfoTmp = {
				index: rowInfo.index + 1
			}
			let subLevel = 0;
			let dataSub = dataProvider.getRC(columnInfo.index, rowInfo.index + 1);
			if (dataSub) {
				cellPropertiesTmp = this.getCellProperties(dataSub, columnInfo, rowInfoTmp);
				subLevel = cellPropertiesTmp && cellPropertiesTmp.level ? cellPropertiesTmp.level : 0;
			}

			if (formattedValue.level < subLevel) {
				formattedValue.fillColor = '#8676D3';

				for (let i = 1; i < 10; i += 1) {
					dataSub = dataProvider.getRC(columnInfo.index, rowInfo.index + i);
					if (dataSub !== undefined) {
						rowInfoTmp.index = rowInfo.index + i;
						cellPropertiesTmp = this.getCellProperties(dataSub, columnInfo, rowInfoTmp);
						const subLevelLoop = cellPropertiesTmp && cellPropertiesTmp.level ? cellPropertiesTmp.level : 0;
						const val = dataSub.getValue();
						if (subLevelLoop !== subLevel) {
							break;
						}
						if (Number(val) !== i - 1) {
							formattedValue.fillColor = '#FD602E';
						}
					} else {
						break;
						// formattedValue.fillColor = '#E17000';
					}
				}
			} else {
				const value = dataNext.getValue();
				if (value === '{ JSON Object }') {
					formattedValue.fillColor = '#FD602E';
				} else {
					const type = typeof value;
					// draw fill based on cell value type
					switch (type) {
					case 'string':
						formattedValue.fillColor = '#49af57';
						break;
					case 'number':
						formattedValue.fillColor = '#73ABCB';
						break;
					case 'boolean':
						formattedValue.fillColor = '#6DD0BA';
						break;
					default:
						formattedValue.fillColor = '#49af57';
						break;
					}
				}
			}
		}
	}

	resetFont(graphics) {
		graphics.setFontName('Verdana');
		graphics.setFontSize(9);
		graphics.setFontStyle(TextFormatAttributes.FontStyle.NORMAL);
		graphics.setFont();
	}

	getStyleProperties(cell, column, row) {
		return this._wsItem.model.getFormatProperties( column.index, row.index);
	}

	getTextProperties(cell, column, row) {
		return this._wsItem.model.getTextFormatProperties(column.index, row.index);
	}

	getCellProperties(cell, column, row) {
		return this._wsItem.model.getCellProperties(column.index, row.index);
	}

	drawCellFill(graphics, data, columnInfo, rowInfo, visibleColumnsInfo, visibleRowsInfo, styleproperties, cellProperties) {
		if (
			styleproperties === undefined ||
			styleproperties.fillcolor === undefined ||
			Number(styleproperties.fillstyle) !== 1
		) {
			if (cellProperties && cellProperties.key) {
				graphics.setFillColor(JSG.theme.filllight);
				graphics.fillRoundedRectangle(
					columnInfo.x + 50,
					rowInfo.y + 50,
					columnInfo.width - 100,
					rowInfo.height - 100,
					0, 100, 0, 100
				);
			}
		} else {
			if (rowInfo.grey) {
				graphics.setTransparency(60);
			}
			const pix = graphics.getCoordinateSystem().deviceToLogX(1);
			this.rect(graphics, columnInfo.x, rowInfo.y, columnInfo.width + pix, rowInfo.height + pix, styleproperties.fillcolor);
			if (rowInfo.grey) {
				graphics.setTransparency(100);
			}
			columnInfo.gridSkipBorders.push({
				y: rowInfo.y,
				height: rowInfo.height
			});

			if (visibleColumnsInfo.length > columnInfo.myIndex + 1) {
				const borders = visibleColumnsInfo[columnInfo.myIndex + 1].gridSkipBorders;
				if (borders) {
					borders.push({
						y: rowInfo.y,
						height: rowInfo.height
					});
				}
			}
		}

		if (styleproperties !== undefined) {
			if (styleproperties.leftborderstyle) {
				const color = styleproperties.leftbordercolor || '#000000';
				if (columnInfo.leftBorders[color] === undefined) {
					columnInfo.leftBorders[color] = {};
				}
				columnInfo.leftBorders[color][rowInfo.y] = {
					height: rowInfo.height,
					style: styleproperties.leftborderstyle,
					borderWidth: styleproperties.leftborderwidth
				};
			}
			if (styleproperties.topborderstyle) {
				const color = styleproperties.topbordercolor || '#000000';
				if (rowInfo.topBorders[color] === undefined) {
					rowInfo.topBorders[color] = {};
				}
				rowInfo.topBorders[color][columnInfo.x] = {
					width: columnInfo.width,
					style: styleproperties.topborderstyle,
					borderWidth: styleproperties.topborderwidth
				};
			}
			if (styleproperties.rightborderstyle && visibleColumnsInfo.length > columnInfo.myIndex + 1) {
				const borders = visibleColumnsInfo[columnInfo.myIndex + 1].leftBorders;
				if (borders) {
					const color = styleproperties.rightbordercolor || '#000000';
					if (borders[color] === undefined) {
						borders[color] = {};
					}
					borders[color][rowInfo.y] = {
						height: rowInfo.height,
						style: styleproperties.rightborderstyle,
						borderWidth: styleproperties.rightborderwidth
					};
				}
			}
			if (styleproperties.bottomborderstyle && visibleRowsInfo.length > rowInfo.myIndex + 1) {
				const borders = visibleRowsInfo[rowInfo.myIndex + 1].topBorders;
				if (borders) {
					const color = styleproperties.bottombordercolor || '#000000';
					if (borders[color] === undefined) {
						borders[color] = {};
					}
					borders[color][columnInfo.x] = {
						width: columnInfo.width,
						style: styleproperties.bottomborderstyle,
						borderWidth: styleproperties.bottomborderwidth
					};
				}
			}
		}
	}

	drawSpecialFunction(graphics, data, columnInfo, rowInfo, visibleColumnsInfo, visibleRowsInfo) {
		const termFunc = data.getExpression().getTerm();
		if (termFunc && termFunc instanceof FuncTerm) {
			switch (termFunc.getFuncId()) {
			case 'CELLCHART': {
				if (termFunc.params && termFunc.params.length > 0 && termFunc.params[0].value) {
					const node = new SheetPlotNode();
					node._parent = this.getItem();
					const yValues = node.getParamInfo(termFunc, 0);
					if (yValues) {
						const view = createView(node);
						node.evaluate();
						const range = yValues.copy();
						const selection = new Selection(this._wsItem.model);
						selection.add(range);
						// range.shiftFromSheet();
						const type = (termFunc.params.length > 1 && termFunc.params[1].value) || 'line';
						node.setSize(columnInfo.width, rowInfo.height);
						node.createSeriesFromSelection(undefined, this._wsItem.model, selection, String(type));
						node.layout();
						if (node.series.length) {
							const serie = node.series[0];
							if (termFunc.params.length > 2 && termFunc.params[2].value) {
								serie.format.lineColor = termFunc.params[2].value;
							}
							if (termFunc.params.length > 3 && termFunc.params[3].value) {
								serie.format.fillColor = termFunc.params[3].value;
							}
							if (termFunc.params.length > 4 && termFunc.params[4].value) {
								serie.marker.style = termFunc.params[4].value;
								serie.marker.size = 2;
							}
							if (termFunc.params.length > 5 && termFunc.params[5].value !== undefined) {
								node.yAxes[0].scale.min = termFunc.params[5].value;
							}
							if (termFunc.params.length > 6 && termFunc.params[6].value && node.yAxes[0].scale) {
								node.yAxes[0].scale.max = termFunc.params[6].value;
							}
							const rect = new ChartRect(columnInfo.x, rowInfo.y, columnInfo.x + columnInfo.width,
								rowInfo.y + rowInfo.height);
							switch (serie.type) {
							case 'pie':
							case 'doughnut':
								view.drawCircular(graphics, node, rect, serie, 0);
								break;
							case 'gauge':
								view.drawGauge(graphics, node, rect, serie, 0);
								break;
							case 'boxplot':
								view.drawBoxPlot(graphics, node, rect, serie, 0);
								break;
							default:
								view.drawCartesian(graphics, node, rect, serie, 0);
								break;
							}
						}
					}
				}
				break;
			}
			case 'SELECT': {
				graphics.setFillColor('#888888');

				const pts = [
					{x: columnInfo.x + columnInfo.width - 400, y: rowInfo.y + rowInfo.height / 2 - 80},
					{x: columnInfo.x + columnInfo.width - 150, y: rowInfo.y + rowInfo.height / 2 - 80},
					{x: columnInfo.x + columnInfo.width - 275, y: rowInfo.y + rowInfo.height / 2 + 80}
				];

				graphics.fillPolyline(pts, true);
				graphics.setFillColor('#000000');
				break;
			}
			case 'BAR': {
				const cellValue = data.getValue();
				if (cellValue === undefined) {
					return;
				}
				const params = String(cellValue).split(';');
				let value = 0;
				let direction = false;
				let fillColor = '#00FF00';
				let lineColor;
				params.forEach((param, index) => {
					switch (index) {
					case 0:
						value = Number(param);
						if (Number.isNaN(value)) {
							value = param.length > 0 && param !== 'false' ? 1 : 0;
						}
						break;
					case 1:
						direction = Number(param);
						break;
					case 2:
						fillColor = param === 'undefined' || param === '' ? '#00FF00' : param;
						break;
					case 3:
						lineColor = param === 'undefined' || param === '' ? undefined : param;
						break;
					default:
						break;
					}
				});
				if (rowInfo.grey) {
					graphics.setTransparency(60);
				}
				if (direction) {
					this.rect(graphics, columnInfo.x, rowInfo.y + rowInfo.height - rowInfo.height * value, columnInfo.width + 20, rowInfo.height * value,
						fillColor);
				} else {
					this.rect(graphics, columnInfo.x, rowInfo.y, columnInfo.width * value + 20, rowInfo.height + 20,
						fillColor);
				}
				if (rowInfo.grey) {
					graphics.setTransparency(100);
				}
				columnInfo.gridSkipBorders.push({
					y: rowInfo.y,
					height: rowInfo.height
				});

				if (visibleColumnsInfo.length > columnInfo.myIndex + 1) {
					const borders = visibleColumnsInfo[columnInfo.myIndex + 1].gridSkipBorders;
					if (borders) {
						borders.push({
							y: rowInfo.y,
							height: rowInfo.height
						});
					}
				}

				if (lineColor !== undefined) {
					const color = lineColor;
					if (columnInfo.leftBorders[color] === undefined) {
						columnInfo.leftBorders[color] = {};
					}
					columnInfo.leftBorders[color][rowInfo.y] = {
						height: rowInfo.height,
						style: 1,
						borderWidth: 1
					};
					if (rowInfo.topBorders[color] === undefined) {
						rowInfo.topBorders[color] = {};
					}
					rowInfo.topBorders[color][columnInfo.x] = {
						width: columnInfo.width,
						style: 1,
						borderWidth: 1
					};
					if (visibleColumnsInfo.length > columnInfo.myIndex + 1) {
						const borders = visibleColumnsInfo[columnInfo.myIndex + 1].leftBorders;
						if (borders) {
							if (borders[color] === undefined) {
								borders[color] = {};
							}
							borders[color][rowInfo.y] = {
								height: rowInfo.height,
								style: 1,
								borderWidth: 1
							};
						}
					}
					if (visibleRowsInfo.length > rowInfo.myIndex + 1) {
						const borders = visibleRowsInfo[rowInfo.myIndex + 1].topBorders;
						if (borders) {
							if (borders[color] === undefined) {
								borders[color] = {};
							}
							borders[color][columnInfo.x] = {
								width: columnInfo.width,
								style: 1,
								borderWidth: 1
							};
						}
					}
				}
				break;
			}
			default:
				break;
			}
		}
	}
	drawInfoMarkers(graphics, data, columnInfo, rowInfo) {
		if (data.attributes && data.attributes.note !== undefined) {
			drawMarker(graphics, '#ff9100', [
				{ x: columnInfo.x + columnInfo.width - 200, y: rowInfo.y},
				{ x: columnInfo.x + columnInfo.width, y: rowInfo.y },
				{ x: columnInfo.x + columnInfo.width, y: rowInfo.y + 200 }
			]);
		}
		if (data.values || data.hasHiddenPayload()) {
			drawMarker(graphics, '#fdbf01', [
				{ x: columnInfo.x, y: rowInfo.y + rowInfo.height - 200 },
				{ x: columnInfo.x + 200, y: rowInfo.y + rowInfo.height },
				{ x: columnInfo.x, y: rowInfo.y + rowInfo.height }
			]);
		}
		// draw error marker
		if (data.errorInfo && data.getExpression()) {
			const term = data.getExpression().getTerm();
			const markerColor = FuncTerm.showName(term) && data.hasErrorValue ? '#F4BB38' : '#FF0000';
			drawMarker(graphics, markerColor, [
				{ x: columnInfo.x, y: rowInfo.y },
				{ x: columnInfo.x + 300, y: rowInfo.y },
				{ x: columnInfo.x, y: rowInfo.y + 300 }
			]);
		}
	}

	drawValue(graphics, dataProvider, data, columnInfo, rowInfo, visibleColumnsInfo, styleProperties, cellProperties) {
		const textproperties = this.getTextProperties(data, columnInfo, rowInfo);
		let font = false;
		if (textproperties) {
			if (textproperties.fontname) {
				graphics.setFontName(textproperties.fontname);
				font = true;
			}
			if (textproperties.fontsize) {
				graphics.setFontSize(textproperties.fontsize);
				font = true;
			}
			if (textproperties.fontstyle) {
				graphics.setFontStyle(textproperties.fontstyle);
				font = true;
			}
			if (font) {
				graphics.setFont();
			}
		}

		const formattedValue = this.getFormattedValue(graphics, data, textproperties, styleProperties, columnInfo.width);
		if (formattedValue.value === undefined || formattedValue.value === null) {
			if (font) {
				this.resetFont(graphics);
			}
			return;
		}
		if (formattedValue.formattedValue.length > 500) {
			formattedValue.formattedValue = `${formattedValue.formattedValue.slice(0, 500)   }...`;
		}

		if (columnInfo.index === 1) {
			const state =
				formattedValue.value === 0 ||
				formattedValue.value === '' ||
				formattedValue.value === false ||
				formattedValue.value === 'false';
			if (this._showFormulas === false && columnInfo.width < 1100) {
				graphics.setTransparency(JSG.theme.ifTransparency);
				this.rect(
					graphics,
					columnInfo.x,
					rowInfo.y,
					columnInfo.width,
					rowInfo.height,
					state ? '#EF7F7D' : '#9CCDC3'
				);
				graphics.setTransparency(100);
				if (font) {
					this.resetFont(graphics);
				}
				return;
			}
		}

		if (cellProperties !== undefined) {
			this.applyFormatFromKey(dataProvider, data, columnInfo, rowInfo, formattedValue, cellProperties);
		}

		if (formattedValue.fillColor) {
			if (rowInfo.grey) {
				graphics.setTransparency(60);
			}
			if (formattedValue.frame) {
				graphics.setFillColor(formattedValue.fillColor);
				graphics.fillRoundedRectangle(
					columnInfo.x + formattedValue.level * 150 + 50,
					rowInfo.y + 50,
					columnInfo.width - formattedValue.level * 150 - 100,
					rowInfo.height - 100,
					100, 0, 100, 0
				);
			} else if (formattedValue.rounded) {
				const left = formattedValue.rounded === 'full' || formattedValue.rounded === 'left';
				const right = formattedValue.rounded === 'full' || formattedValue.rounded === 'right';
				graphics.setFillColor(formattedValue.fillColor);
				graphics.fillRoundedRectangle(
					columnInfo.x,
					rowInfo.y,
					columnInfo.width,
					rowInfo.height,
					left ? 100 : 0, right ? 100 : 0, left ? 100 : 0, right ? 100 : 0
				);
			} else {
				this.rect(
					graphics,
					columnInfo.x,
					rowInfo.y,
					columnInfo.width,
					rowInfo.height,
					formattedValue.fillColor
				);
			}
			if (rowInfo.grey) {
				graphics.setTransparency(100);
			}
			if (formattedValue.frame !== true && columnInfo.gridSkipBorders) {
				columnInfo.gridSkipBorders.push({
					y: rowInfo.y,
					height: rowInfo.height
				});
				if (visibleColumnsInfo.length > columnInfo.myIndex + 1) {
					const borders = visibleColumnsInfo[columnInfo.myIndex + 1].gridSkipBorders;
					if (borders) {
						borders.push({
							y: rowInfo.y,
							height: rowInfo.height
						});
					}
				}
			}
		}
		if (formattedValue.color) {
			graphics.setFillColor(formattedValue.color);
		} else if  (textproperties.fontcolor) {
			graphics.setFillColor(textproperties.fontcolor);
		} else {
			graphics.setFillColor(JSG.theme.text);
		}
		if (formattedValue.bold) {
			graphics.setFontStyle(TextFormatAttributes.FontStyle.BOLD);
			font = true;
			graphics.setFont();
		}
		if (rowInfo.grey) {
			graphics.setTransparency(60);
		}

		let x = 0;
		let clipX;
		let clipWidth;
		let alignment =
			textproperties === undefined || textproperties.halign === undefined ? 3 : Number(textproperties.halign);
		if (alignment === 3) {
			alignment = formattedValue.alignment === undefined ? 0 : formattedValue.alignment;
		}
		if (formattedValue.forcedAlignment !== undefined) {
			alignment = formattedValue.forcedAlignment;
		}

		graphics.setTextAlignment(alignment);

		switch (alignment) {
			case 0:
				x = columnInfo.x + 75;
				if (formattedValue.level !== undefined) {
					x += formattedValue.level * 150 + 50;
					// y += formattedValue.level ? 50 : 0;
				}
				break;
			case 1:
				x = columnInfo.x + columnInfo.width / 2;
				break;
			case 2:
				x = columnInfo.x + columnInfo.width - 75;
				break;
		}

		if (formattedValue.clip) {
			clipX = columnInfo.x;
			clipWidth = columnInfo.width - (formattedValue.clipSpace ? formattedValue.clipSpace : 0);
		} else {
			let width = graphics
				.getCoordinateSystem()
				.deviceToLogX(graphics.measureText(formattedValue.formattedValue).width, true);
			if (width > columnInfo.width) {
				// clip, if text reaches into next cell
				const clipInfo = this.getCellSpace(
					dataProvider,
					columnInfo.index,
					rowInfo.index,
					{ x: columnInfo.x, width: columnInfo.width },
					alignment
				);
				if (clipInfo.clip) {
					clipX = clipInfo.x;
					clipWidth = clipInfo.width;
					width = Math.min(width, clipWidth);
				}
				visibleColumnsInfo.forEach((info) => {
					switch (alignment) {
						case 0:
							if (
								info.x > x &&
								info.x < x + width &&
								(clipInfo.clip === false || info.x < clipInfo.x + clipInfo.width)
							) {
								info.skipBorders[rowInfo.y] = rowInfo.height;
								info.gridSkipBorders.push({
									y: rowInfo.y,
									height: rowInfo.height
								});
							}
							break;
						case 1:
							if (
								(info.x > x &&
									info.x < x + width / 2 &&
									(clipInfo.clip === false || info.x < clipInfo.x + clipInfo.width)) ||
								(info.x <= x && info.x > x - width / 2 && (clipInfo.clip === false || info.x > clipInfo.x))
							) {
								info.skipBorders[rowInfo.y] = rowInfo.height;
								info.gridSkipBorders.push({
									y: rowInfo.y,
									height: rowInfo.height
								});
							}
							break;
						case 2:
							if (info.x <= x && info.x > x - width && (clipInfo.clip === false || info.x > clipInfo.x)) {
								info.skipBorders[rowInfo.y] = rowInfo.height;
								info.gridSkipBorders.push({
									y: rowInfo.y,
									height: rowInfo.height
								});
							}
							break;
					}
				});
			}
		}
		if (clipWidth) {
			graphics.save();
			graphics.beginPath();
			graphics.rect(clipX + 75, rowInfo.y, clipWidth - 75, rowInfo.height);
			graphics.clip();
		}

		let y = 0;
		const name = textproperties && textproperties.fontname ? textproperties.fontname : 'Verdana';
		const size = textproperties && textproperties.fontsize ? textproperties.fontsize : 9;
		const metrics = GraphUtils.getFontMetricsEx(name, size);
		const valign =
			textproperties === undefined || textproperties.valign === undefined ? 2 : Number(textproperties.valign);

		switch (valign) {
		case 0:
				y = rowInfo.y + metrics.lineheight + Math.max(50, metrics.lineheight * 0.075);
				graphics.setTextBaseline('bottom');
				break;
			case 1: {
				const height = graphics.getCoordinateSystem().deviceToLogYNoZoom(
					graphics.measureText('H').actualBoundingBoxAscent, true);
				y = rowInfo.y + rowInfo.height / 2 - height / 2 * 0.85;
				graphics.setTextBaseline('top');
				break;
			}
			case 2:
			default:
				graphics.setTextBaseline('bottom');
				y = rowInfo.y + rowInfo.height - Math.max(50, metrics.lineheight * 0.075);
				break;
		}

		graphics.fillText(formattedValue.formattedValue, x, y);

		if (formattedValue.loop) {
			const textWidth = graphics
				.getCoordinateSystem()
				.deviceToLogX(graphics.measureText(formattedValue.formattedValue).width, true);
			let xLoop;
			switch (alignment) {
			case 0:
				xLoop = x + textWidth + 100;
				break;
			case 1:
				xLoop = x + textWidth / 2 + 100;
				break;
			case 2:
				xLoop = x - textWidth - 500;
				break;
			}

			graphics.drawImage(
				JSG.imagePool.get('loopw'),
				xLoop,
				y - (valign === 1 ? 0 : 300),
				400,
				300);
		}

		if (clipWidth) {
			graphics.restore();
		}

		if (formattedValue.formattedValue === '!#MeasureClientUpdates#!') {
			if (!data._info) {
				data._info = {};
			}
			data._info.keep = true;
			data._info.xvalue = 'time';
			if (!data._info.values) {
				data._info.values = {};
			}
			if (!data._info.values.time) {
				data._info.values.time = [];
				data._info.values.updates = [];
			}
			if (data._info.values.time.length < 1000) {
				const now = new Date();
				now.setMilliseconds(0);
				const xlDate = dataProvider.JSDateToExcelDate(now);
				let index;
				for (let i = data._info.values.time.length - 1; i >= 0; i -= 1) {
					if (data._info.values.time[i] === xlDate) {
						index = i;
						break;
					}
				}
				if (index === undefined) {
					data._info.values.time.push(xlDate);
					data._info.values.updates.push(1);
				} else {
					data._info.values.updates[index] += 1;
				}
			}
		}

		graphics.setFillColor(JSG.theme.text);

		if (font) {
			this.resetFont(graphics);
		}
		if (rowInfo.grey) {
			graphics.setTransparency(100);
		}
	}

	getCellSpace(dataProvider, colIndex, rowIndex, clipRect, alignment) {
		const isDataEmpty = (lcell) =>
			lcell === undefined ||
			lcell.getValue() === undefined ||
			lcell.getValue() === null ||
			lcell.getValue().toString() === '';
		const columns = this._columns;
		let clip = false;
		let column;
		let size;
		let dataNext;
		const row = dataProvider.getRow(rowIndex);

		switch (alignment) {
			case 0:
				column = colIndex + 1;
				while (column < row.length) {
					size = columns.getSectionSize(column);
					if (size) {
						dataNext = dataProvider.getRC(column, rowIndex);
						if (isDataEmpty(dataNext)) {
							clipRect.width += size;
						} else {
							clip = true;
							break;
						}
					}
					column += 1;
				}
				break;
			case 1: {
				let rClip = false;
				column = colIndex - 1;
				while (column >= 0) {
					size = columns.getSectionSize(column);
					if (size) {
						dataNext = dataProvider.getRC(column, rowIndex);
						if (isDataEmpty(dataNext)) {
							clipRect.x -= size;
							clipRect.width += size;
						} else {
							clip = true;
							break;
						}
					}
					column -= 1;
				}
				column = colIndex + 1;
				while (column < row.length) {
					size = columns.getSectionSize(column);
					if (size) {
						dataNext = dataProvider.getRC(column, rowIndex);
						if (isDataEmpty(dataNext)) {
							clipRect.width += size;
						} else {
							clip = true;
							rClip = true;
							break;
						}
					}
					column += 1;
				}
				if (rClip === false) {
					// in case it clips to the left, but not to the right and there are no cells
					clipRect.width = 60000;
				}
				break;
			}
			case 2:
				column = colIndex - 1;
				while (column >= 0) {
					size = columns.getSectionSize(column);
					if (size) {
						dataNext = dataProvider.getRC(column, rowIndex);
						if (isDataEmpty(dataNext)) {
							clipRect.x -= size;
							clipRect.width += size;
						} else {
							clip = true;
							break;
						}
					}
					column -= 1;
				}
				break;
		}

		clipRect.clip = clip;

		return clipRect;
	}

	getVisibleColumnSections(rect, viewRect) {
		let x = rect.x;
		let width;
		const info = [];

		this._columns.iterateSections((section, index) => {
			if (x > viewRect.x + viewRect.width) {
				return false;
			}
			width = this._columns.getSectionSize(index);
			if (width && x + width >= viewRect.x) {
				const colInfo = {
					section,
					index,
					myIndex: info.length,
					x,
					width,
					gridSkipBorders: [],
					skipBorders: {},
					leftBorders: {}
				};
				info.push(colInfo);
			}
			x += width;
			return true;
		});

		return info;
	}

	getVisibleRowsSections(rect, viewRect, visibleColumnsInfo) {
		let y = rect.y;
		let height;
		const info = [];
		const dataProvider = this._wsItem.cells;
		const columnCnt = this._wsItem.model.maxColumns;

		this._rows.iterateSections((section, index) => {
			if (y > viewRect.y + viewRect.height) {
				return false;
			}
			height = this._rows.getSectionSize(index);
			if (height && y + height >= viewRect.y) {
				const rowInfo = {
					section,
					index,
					myIndex: info.length,
					y,
					height,
					topBorders: {}
				};

				// IF Column
				let data = dataProvider.getRC(1, index);
				if (data !== undefined) {
					const value = data.getValue();
					if (value !== undefined) {
						const state = value === 0 || value === '' || value === false || value === 'false';
						rowInfo.grey = this._greyIfRows && (this._editMode || state);
					}
				}

				// get the cell left and right to the visible area, which might impact the view for each row
				if (visibleColumnsInfo.length) {
					let pos = visibleColumnsInfo[0].index;
					let width;
					while (pos > 0) {
						pos -= 1;
						data = dataProvider.getRC(pos, index);
						width = this._columns.getSectionSize(pos)
						if (width && data !== undefined && data.getValue() !== undefined && data.getValue() !== null && data.getValue().toString() !== '') {
							rowInfo.leftCellInfo = {
								section: this._columns.getSection(pos),
								index: pos,
								x: this._columns.getSectionPos(pos),
								width
							};
							break;
						}
					}
					pos = visibleColumnsInfo[visibleColumnsInfo.length - 1].index;
					const maxCol = Math.min(columnCnt, dataProvider.getRowSize(index));
					while (pos < maxCol) {
						pos += 1;
						data = dataProvider.getRC(pos, index);
						width = this._columns.getSectionSize(pos)
						if (width && data !== undefined && data.getValue() !== undefined) {
							rowInfo.rightCellInfo = {
								section: this._columns.getSection(pos),
								index: pos,
								x: this._columns.getSectionPos(pos),
								width
							};
							break;
						}
					}
				}
				info.push(rowInfo);
			}
			y += height;
			return true;
		});

		return info;
	}

	drawGrid(graphics, rect, viewRect) {
		const rows = this.getRows();
		if (!rows) {
			// for feedback
			return;
		}

		let i;
		let n;
		const startX = rect.x;
		const endX = rect.getRight();
		let startY = rect.y;
		let endY;

		graphics.beginPath();
		graphics.setLineColor(JSG.theme.grid);

		for (i = 0, n = rows.getSections(); i < n; i += 1) {
			startY += rows.getSectionSize(i);
			endY = startY;
			if (startY > viewRect.y) {
				graphics.moveTo(startX, startY);
				graphics.lineTo(endX, endY);
			}
			if (startY > viewRect.getBottom()) {
				break;
			}
		}

		graphics.stroke();
	}

	drawCopyMarker(graphics) {
		if (
			JSG.clipSheet === undefined ||
			JSG.clipSheet.range === undefined ||
			JSG.clipSheet.range.sheet !== this._wsItem.model
		) {
			return;
		}

		const ws = this._wsItem;
		const selrect = ws.getCellRect(JSG.clipSheet.range);

		graphics.setLineColor('#FFFFFF');
		graphics.setLineStyle(FormatAttributes.LineStyle.SOLID);
		graphics.drawRect(selrect);
		graphics.setLineColor('#000000');
		graphics.setLineStyle(FormatAttributes.LineStyle.DASH);
		graphics.drawRect(selrect);

		graphics.setLineStyle(FormatAttributes.LineStyle.SOLID);
	}

	drawRange(graphics, selection) {
		let selrect;
		const ws = this._wsItem;
		const corner = JSG.rectCache.get();
		const size = 60;

		corner.width = size;
		corner.height = size;

		graphics.setLineStyle(FormatAttributes.LineStyle.SOLID);
		graphics.setLineWidth(1);

		selection.getRanges().forEach((range) => {
			if (range.sheet === ws.model) {
				graphics.setFillColor(range._color);
				graphics.setLineColor(range._color);

				selrect = ws.getCellRect(range);

				graphics.setTransparency(10);

				graphics.beginPath();
				graphics.rectangle(selrect);
				graphics.fill();
				graphics.setTransparency(100);
				graphics.stroke(selrect);

				corner.x = selrect.x - size / 2;
				corner.y = selrect.y - size / 2;

				graphics.beginPath();
				graphics.rectangle(corner);

				corner.x = selrect.x + selrect.width - size / 2;

				graphics.rectangle(corner);

				corner.y = selrect.y + selrect.height - size / 2;

				graphics.rectangle(corner);

				corner.x = selrect.x - size / 2;
				graphics.rectangle(corner);
				graphics.fill();
			}
		});

		JSG.rectCache.release(corner);
	}

	drawCellEditRanges(graphics) {
		const cellEditor = CellEditor.getActiveCellEditor();
		if (!cellEditor) {
			return;
		}

		const selection = cellEditor.getEditRanges();
		if (selection) {
			this.drawRange(graphics, selection);
		}

		graphics.setLineWidth(FormatAttributes.LineStyle.HAIRLINE);
	}

	static stringToColour(str) {
		let hash = 0;
		for (let i = 0; i < str.length; i += 1) {
			hash = str.charCodeAt(i) + ((hash << 5) - hash);
		}
		let colour = '#';
		for (let i = 0; i < 3; i += 1) {
			const value = (hash >> (i * 8)) & 0xff;
			colour += `00${value.toString(16)}`.substr(-2);
		}
		return colour;
	}

	drawSelection(graphics, ws, wsView, selection, color, user, viewMode) {
		if (selection.validate() === false) {
			return;
		}
		const cell = selection.getActiveCell();

		graphics.setFillColor(color);
		graphics.setLineColor(color);
		graphics.setLineStyle(FormatAttributes.LineStyle.SOLID);
		graphics.setLineWidth(50);

		let selrect;
		const rangeCnt = selection._ranges.length;
		const pix = graphics.getCoordinateSystem().deviceToLogYNoZoom(1);

		selection._ranges.forEach((range) => {
			selrect = ws.getCellRect(range);

			graphics.setTransparency(10);
			graphics.fillRect(selrect);
			if (rangeCnt === 1) {
				graphics.setTransparency(100);
				graphics.drawRect(selrect);
			}
			if (user) {
				graphics.fillText(user, selrect.getRight(), selrect.getBottom());
			}
		});

		if (rangeCnt !== 1) {
			graphics.setTransparency(100);
		}

		const activeRange = new CellRange();

		activeRange.set(cell.x, cell.y);
		selrect = ws.getCellRect(activeRange);
		graphics.drawRect(selrect);

		if (!viewMode && rangeCnt === 1) {
			const corner = JSG.rectCache.get();
			selrect = ws.getCellRect(selection._ranges[0]);
			corner.set(selrect.getRight() - pix * 3, selrect.getBottom() - pix * 3, pix * 4, pix * 4);
			graphics.setLineColor('#FFFFFF');
			graphics.setLineWidth(1);
			graphics.fillRect(corner);
			corner.width += pix;
			corner.height += pix;
			graphics.drawRect(corner);

			JSG.rectCache.release(corner);
		}
	}

	drawSelections(graphics) {
		const wsView = this._wsView;
		const ws = this._wsItem;
		if (!ws) {
			return;
		}
		const focus =
			this.getGraphView().getFocus() &&
			this.getGraphView()
				.getFocus()
				.getView() === wsView;
		const graph = ws.getGraph();
		const selections = graph._userSelections;

		if (selections !== undefined && !graph.showViewMode) {
			graphics.setTextBaseline('top');
			graphics.setTextAlignment(TextFormatAttributes.TextAlignment.RIGHT);
			graphics.setFontTo('7pt Verdana');

			selections.iterate((selectionId, selectionData) => {
				const otherSelectionId = selectionId.split(';');
				const mySelectionId = String(ws.getSelectionId()).split(';');
				let color = '#AAAAAA';
				let user;
				let selection;
				if (otherSelectionId[0] === mySelectionId[0]) {
					color = focus ? JSG.theme.text : '#777777';
				} else {
					if (otherSelectionId.length > 2) {
						[, , user] = otherSelectionId;
						color = CellsView.stringToColour(user);
					}
					// selection = Selection.fromStringMulti(attr.getValue(), ws.model);
					const { sheetId, selectedCells } = selectionData || {};
					if (ws.model.id === sheetId) selection = Selection.fromStringMulti(selectedCells, ws.model);
				}
				if (selection) {
					this.drawSelection(graphics, ws, wsView, selection, color, user, true);
				}
			});
		}

		const selection = ws.getOwnSelection();
		if (selection && selection.sheet === ws.model) {
			this.drawSelection(graphics, ws, wsView, selection, focus ? JSG.theme.text : '#777777', undefined, graph.showViewMode);
		}

		this.setFont(graphics);
		graphics.setLineWidth(FormatAttributes.LineStyle.HAIRLINE);
	}

	getArrowTarget(sourceRect, targetRect, radius) {

		const angle = Math.atan2(sourceRect.y - targetRect.y, sourceRect.x - targetRect.x);
		return {
			x: targetRect.x + Math.cos(angle) * radius,
			y: targetRect.y + Math.sin(angle) * radius,
		}
	}

	drawTextBox(graphics, text, rect, extOffsetX, extOffsetY) {
		const width = graphics
			.getCoordinateSystem()
			.deviceToLogX(graphics.measureText(text).width, true);
		const height = graphics.getCoordinateSystem().deviceToLogYNoZoom(GraphUtils.getFontMetricsEx('Verdana', 7).baselinePx, true);

		graphics.setLineColor(JSG.theme.border);
		graphics.setFillColor(JSG.theme.fill);

		graphics.fillRectangle(rect.x + extOffsetX, rect.y + extOffsetY - height, width + 150, height + 150);
		graphics.drawRectangle(rect.x + extOffsetX, rect.y + extOffsetY - height, width + 150, height + 150);

		graphics.setFillColor(JSG.theme.text);

		graphics.fillText(text, rect.x + extOffsetX + 75, rect.y + extOffsetY);
	}

	drawPredecessors(graphics, rectCells) {
		if (!this._wsItem.predecessors) {
			return;
		}

		graphics.setLineWidth(1);
		graphics.setLineStyle(FormatAttributes.LineStyle.SOLID);
		graphics.setTextBaseline('middle');
		graphics.setFontSize(7);
		graphics.setFont();
		graphics.setTextAlignment(TextFormatAttributes.TextAlignment.LEFT);

		this._wsItem.predecessors.forEach(pre => {
			if (pre.cell.sheet !== this._wsItem.model) {
				return;
			}
			const rectActive = this._wsItem.getCellRect(pre.cell);
			rectActive.x += 200;
			rectActive.y += 200;
			const extOffsetX = rectCells.getRight() > rectActive.x + 4000 ? rectActive.width : -3000;
			let extOffsetY = rectCells.getBottom() > rectActive.y + 4000 ? 2000 : -2000;
			pre.cells.forEach(range => {
				range.shiftFromSheet();
				const rect = this._wsItem.getCellRect(range);
				range.shiftToSheet();
				if (range.sheet !== this._wsItem.model) {
					graphics.setLineColor(JSG.theme.border);
					graphics.setFillColor(JSG.theme.border);
					graphics.beginPath();
					rect.x += 200;
					rect.y += 200;
					// graphics.circle(rectActive.x + rectActive.width, rectActive.y + extOffset, 75);
					graphics.moveTo(rectActive.x + extOffsetX, rectActive.y + extOffsetY);
					graphics.lineTo(rectActive.x, rectActive.y);
					graphics.stroke();
					graphics.fill();
					graphics.drawArrow({ x: rectActive.x + extOffsetX, y: rectActive.y + extOffsetY}, { x: rectActive.x, y: rectActive.y },
						FormatAttributes.ArrowStyle.SIZEABLE_FILLEDARROW, 150, 150);
					this.drawTextBox(graphics, range.toString({sheet: this._wsItem.model, forceName: true, useName: true}), rectActive, extOffsetX, extOffsetY);
					extOffsetY += 500;
				} else {
					graphics.setLineColor(JSG.theme.theme === 'Dark' ? '#6fa8dc' : '#0000FF');
					graphics.setFillColor(JSG.theme.theme === 'Dark' ? '#6fa8dc' : '#0000FF');
					graphics.beginPath();
					if (range.getWidth() > 1 || range.getHeight() > 1) {
						graphics.drawRectangle(rect.x, rect.y, rect.width, rect.height);
					}
					rect.x += 200;
					rect.y += 200;
					graphics.circle(rect.x, rect.y, 75);
					graphics.moveTo(rect.x, rect.y);

					const target = this.getArrowTarget(rect, rectActive, 75);
					graphics.lineTo(target.x, target.y);

					graphics.stroke();
					graphics.fill();
					graphics.drawArrow({ x: rect.x, y: rect.y }, { x: target.x, y: target.y },
						FormatAttributes.ArrowStyle.SIZEABLE_FILLEDARROW, 150, 150);
				}
			})
		});
	}

	drawDependants(graphics, rectCells) {
		if (!this._wsItem.dependants) {
			return;
		}

		graphics.setLineWidth(1);
		graphics.setLineStyle(FormatAttributes.LineStyle.SOLID);
		graphics.setTextBaseline('middle');
		graphics.setFontSize(7);
		graphics.setFont();
		graphics.setTextAlignment(TextFormatAttributes.TextAlignment.LEFT);

		this._wsItem.dependants.forEach(pre => {
			if (pre.cell.sheet !== this._wsItem.model) {
				return;
			}
			const rectActive = this._wsItem.getCellRect(pre.cell);
			rectActive.x += 200;
			rectActive.y += 200;
			const extOffsetX = rectCells.getRight() > rectActive.x + 4000 ? rectActive.width : -3000;
			let extOffsetY = rectCells.getBottom() > rectActive.y + 4000 ? 2000 : -2000;
			pre.cells.forEach(range => {
				const rect = this._wsItem.getCellRect(range);
				if (range.sheet !== this._wsItem.model) {
					graphics.setLineColor(JSG.theme.border);
					graphics.setFillColor(JSG.theme.border);
					graphics.beginPath();
					rect.x += 200;
					rect.y += 200;
					graphics.circle(rectActive.x, rectActive.y, 75);
					graphics.moveTo(rectActive.x, rectActive.y);
					graphics.lineTo(rectActive.x + extOffsetX, rectActive.y + extOffsetY);
					graphics.stroke();
					graphics.fill();
					graphics.drawArrow(
						{ x: rectActive.x, y: rectActive.y},
						{ x: rectActive.x + extOffsetX, y: rectActive.y + extOffsetY },
						FormatAttributes.ArrowStyle.SIZEABLE_FILLEDARROW, 150, 150);
					range.shiftToSheet();
					this.drawTextBox(graphics, range.toString({sheet: this._wsItem.model, forceName: true, useName: true}), rectActive, extOffsetX, extOffsetY);
					range.shiftFromSheet();
					extOffsetY += 500;
				} else {
					graphics.setLineColor(JSG.theme.theme === 'Dark' ? '#6fa8dc' : '#0000FF');
					graphics.setFillColor(JSG.theme.theme === 'Dark' ? '#6fa8dc' : '#0000FF');
					graphics.beginPath();
					if (range.getWidth() > 1 || range.getHeight() > 1) {
						graphics.drawRectangle(rect.x, rect.y, rect.width, rect.height);
					}
					rect.x += 200;
					rect.y += 200;
					graphics.circle(rectActive.x, rectActive.y, 75);

					graphics.moveTo(rectActive.x, rectActive.y);

					const target = this.getArrowTarget(rectActive, rect, 75);
					graphics.lineTo(target.x, target.y);

					graphics.stroke();
					graphics.fill();
					graphics.drawArrow({ x: rectActive.x, y: rectActive.y }, { x: target.x, y: target.y },
						FormatAttributes.ArrowStyle.SIZEABLE_FILLEDARROW, 150, 150);
				}
			})
		});
	}

	drawSearchResult(graphics) {
		const graph = this._wsItem.getGraph();
		if (!graph.searchResult) {
			return;
		}

		graphics.setTransparency(20);
		graphics.setFillColor('#00FF00');
		graphics.beginPath();

		graph.searchResult.forEach(range => {
			if (range.sheet === this._wsItem.model) {
				const rect = this._wsItem.getCellRect(range);
				graphics.fillRectangle(rect.x, rect.y, rect.width, rect.height);
			}
		});

		graphics.fill();
		graphics.setTransparency(100);

		if (graph.activeSearchIndex !== -1) {
			const range = graph.searchResult[graph.activeSearchIndex];
			if (range.sheet === this._wsItem.model) {
				const rect = this._wsItem.getCellRect(range);
				graphics.setLineWidth(50);
				graphics.setLineColor(JSG.theme.border);
				graphics.drawRectangle(rect.x - 100, rect.y - 100, rect.width + 200, rect.height + 200);
				graphics.stroke();

				graphics.setLineWidth(1);
			}
		}
	}

	drawTourHighlights(graphics) {
		const graph = this._wsItem.getGraph();
		if (!graph.tourHighlights) {
			return;
		}

		graphics.beginPath();
		graphics.setLineWidth(50);
		graphics.setLineColor('#FD602E');

		graph.tourHighlights.forEach(rect => {
				// const rect = this._wsItem.getCellRect(range);
			graphics.drawRectangle(rect.x - 100, rect.y - 100, rect.width + 200, rect.height + 200);
		});

		graphics.stroke();
		graphics.setLineWidth(1);
	}

	rectangle(graphics, rect, fillColor, transparency) {
		graphics.setFillColor(fillColor);
		if (transparency) {
			graphics.setTransparency(transparency);
		}
		graphics.beginPath();
		graphics.rectangle(rect);
		graphics.fill();
		if (transparency) {
			graphics.setTransparency(100);
		}
	}

	rect(graphics, x, y, width, height, fillColor) {
		graphics.setFillColor(fillColor);
		graphics.beginPath();
		graphics.rect(x, y, width, height);
		graphics.fill();
	}

	setFont(graphics) {
		graphics.setFontName('Verdana');
		graphics.setFontStyle(TextFormatAttributes.FontStyle.NORMAL);
		graphics.setFontSize(9);
		graphics.setFillColor('#333333');
		graphics.setTextAlign(TextFormatAttributes.TextAlignment.LEFT);
		graphics.setTextBaseline('middle');
		graphics.setFont();
	}

	adaptHighlight(highlight) {
		let view = this;
		while (view) {
			if (view instanceof ContentNodeView) {
				const orgItem = highlight.getOriginalItem();
				const rect = orgItem
					.getParent()
					.getParent()
					.getTranslatedBoundingBox()
					.getBoundingRectangle();
				highlight.getFeedbackItem().setSize(rect.width, rect.height);
				highlight.getFeedbackItem().setOrigin(rect.x, rect.y);
				break;
			}
			view = view.getParent();
		}
	}

	getColumns() {
		return this._wsItem ? this._wsItem.getColumns() : undefined;
	}

	getRows() {
		return this._wsItem ? this._wsItem.getRows() : undefined;
	}

	getDragTarget() {
		return this.getWorksheetView();
	}

	getWorksheetView() {
		let parent = this.getParent();
		if (parent === undefined) {
			return undefined;
		}

		if (parent instanceof ContentPaneView) {
			parent = parent.getParent();
			if (parent) {
				return parent.getContentView();
			}
		} else {
			return parent;
		}

		return undefined;
	}

	getWorksheetNode() {
		return this.getItem()
			.getParent()
			.getParent();
	}

	getScrollOffset() {
		const ws = this.getWorksheetView();
		if (ws) {
			return ws.getScrollOffset();
		}

		return new Point(0, 0);
	}

	checkMaximumImageDimensions(image) {
		if (image.width * image.height < 500 * 500) {
			return true;
		}

		const view = this.getWorksheetView();
		view.notifyMessage({ id: 'SheetMessage.imageDrop' });

		return false;
	}
}
